// src/pages/Projects.jsx
import React, { useEffect, useMemo, useState } from "react";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import Logo from "../components/common/logo";
import SeoMeta, { SITE_URL, makeDescription } from "../components/common/SeoMeta";
import { fetchPublicContent } from "../data/publicContent";

import INFO from "../data/user";
import SEO from "../data/seo";


import "./styles/projects.css";

// Media appearances are now managed live from the Admin Dashboard → Media tab
// and stored in the Firestore "media" collection. Each doc looks like:
//   { type: "video" | "radio" | "press", platform, title, url, cta, order }

const TYPE_META = {
	video: { label: "Video", icon: "▶" },
	radio: { label: "Audio", icon: "◎" },
	press: { label: "Press", icon: "✦" },
};

// ── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Extracts a YouTube video ID from any of these URL shapes:
 *   https://www.youtube.com/watch?v=VIDEO_ID
 *   https://www.youtube.com/watch?app=desktop&v=VIDEO_ID
 *   https://youtu.be/VIDEO_ID
 *   https://youtu.be/VIDEO_ID?si=...
 * Returns null if the URL is not a YouTube link.
 */
function getYouTubeId(url) {
	try {
		const u = new URL(url);
		if (u.hostname === "youtu.be") {
			const id = u.pathname.slice(1).split("/")[0];
			return /^[\w-]{11}$/.test(id) ? id : null;
		}
		if (u.hostname === "youtube.com" || u.hostname.endsWith(".youtube.com")) {
			const id = u.searchParams.get("v");
			return id && /^[\w-]{11}$/.test(id) ? id : null;
		}
	} catch {
		// not a valid URL
	}
	return null;
}

function safeExternalUrl(value) {
	try {
		const url = new URL(value);
		return ["https:", "http:"].includes(url.protocol) ? url.toString() : null;
	} catch {
		return null;
	}
}

function getYouTubeThumbnail(url) {
	const id = getYouTubeId(url);
	return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}

function normaliseMediaItem(id, value) {
	if (!value || typeof value !== "object") return null;
	const title = typeof value.title === "string" ? value.title.trim() : "";
	const url = safeExternalUrl(value.url);
	if (!title || !url) return null;
	const type = Object.prototype.hasOwnProperty.call(TYPE_META, value.type) ? value.type : "press";
	return {
		id,
		type,
		title,
		url,
		platform: typeof value.platform === "string" ? value.platform.trim() : "",
		cta: typeof value.cta === "string" && value.cta.trim()
			? value.cta.trim()
			: type === "video" ? "Watch video" : type === "radio" ? "Listen now" : "Read more",
		description: typeof value.description === "string" ? value.description.trim() : "",
		order: Number.isFinite(Number(value.order)) ? Number(value.order) : 0,
	};
}

function normalisePlatformKey(value) {
	return String(value || "")
		.normalize("NFKD")
		.replace(/\p{Diacritic}/gu, "")
		.toLocaleLowerCase()
		.replace(/&/g, "and")
		.replace(/[^a-z0-9]/g, "");
}

function editDistance(left, right) {
	const previous = Array.from({ length: right.length + 1 }, (_, index) => index);
	for (let i = 1; i <= left.length; i++) {
		const current = [i];
		for (let j = 1; j <= right.length; j++) {
			current[j] = Math.min(
				current[j - 1] + 1,
				previous[j] + 1,
				previous[j - 1] + (left[i - 1] === right[j - 1] ? 0 : 1)
			);
		}
		previous.splice(0, previous.length, ...current);
	}
	return previous[right.length];
}

function platformNamesMatch(left, right) {
	if (left === right) return true;
	const shorterLength = Math.min(left.length, right.length);
	if (shorterLength < 4) return false;
	const allowedTypos = Math.min(2, Math.max(1, Math.floor(shorterLength * 0.18)));
	return editDistance(left, right) <= allowedTypos;
}

function groupMediaByPlatform(items) {
	const groups = [];
	for (const item of items) {
		const label = item.platform.trim();
		const key = normalisePlatformKey(label);
		let group = key
			? groups.find((candidate) => candidate.keys.some((candidateKey) => platformNamesMatch(key, candidateKey)))
			: groups.find((candidate) => candidate.key === "");

		if (!group) {
			group = { key, keys: [], labels: new Map(), items: [] };
			groups.push(group);
		}
		if (key && !group.keys.includes(key)) group.keys.push(key);
		if (label) group.labels.set(label, (group.labels.get(label) || 0) + 1);
		group.items.push(item);
	}

	return groups.map((group) => ({
		...group,
		label: group.labels.size
			? [...group.labels.entries()].sort((a, b) => b[1] - a[1] || a[0].length - b[0].length || a[0].localeCompare(b[0]))[0][0]
			: "Other platforms",
	}));
}

// ── Media card ─────────────────────────────────────────────────────────────────

const MediaCard = ({ item }) => {
	const meta      = TYPE_META[item.type] || TYPE_META.press;
	const thumbnail = item.type === "video" ? getYouTubeThumbnail(item.url) : null;
	const [thumbnailFailed, setThumbnailFailed] = useState(false);

	useEffect(() => {
		setThumbnailFailed(false);
	}, [thumbnail]);

	return (
		<a
			className={`media-card media-card--${item.type}`}
			href={item.url}
			target="_blank"
			rel="noopener noreferrer"
			aria-label={`${item.title} — ${item.platform || meta.label} (opens in a new tab)`}
		>
			{/* YouTube thumbnail — video cards only */}
			{thumbnail && !thumbnailFailed && (
				<div className="media-card__thumbnail">
					<img
						src={thumbnail}
						alt={`Thumbnail for: ${item.title}`}
						className="media-card__thumb-img"
						loading="lazy"
						onError={() => setThumbnailFailed(true)}
					/>
					<div className="media-card__play" aria-hidden="true">
						<svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
							<circle cx="18" cy="18" r="18" fill="rgba(0,0,0,0.55)" />
							<polygon points="14,11 27,18 14,25" fill="#ffffff" />
						</svg>
					</div>
				</div>
			)}
			{thumbnail && thumbnailFailed && (
				<div className="media-card__thumbnail media-card__thumbnail--fallback" aria-hidden="true">
					<span>{meta.label}</span>
				</div>
			)}

			<div className="media-card__body">
				<div className="media-card__top">
					<span className={`media-badge media-badge--${item.type}`}>
						<span className="media-badge__icon" aria-hidden="true">{meta.icon}</span>
						{meta.label}
					</span>
				</div>
				<p className="media-card__title">{item.title}</p>
				<div className="media-card__footer">
					<span>{item.cta}</span>
					<span className="media-card__arrow" aria-hidden="true">↗</span>
				</div>
			</div>
		</a>
	);
};

// ── Page ───────────────────────────────────────────────────────────────────────

const Projects = () => {
	const [mediaItems, setMediaItems] = useState([]);
	const [loadingMedia, setLoadingMedia] = useState(true);
	const [mediaError, setMediaError] = useState(null);
	const [retryKey, setRetryKey] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [activeType, setActiveType] = useState("all");

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	// Public media is served through the shared, short-lived API cache.
	useEffect(() => {
		setLoadingMedia(true);
		setMediaError(null);
		let cancelled = false;
		fetchPublicContent("media")
			.then((items) => {
				if (cancelled) return;
				const docs = items
					.map((item) => normaliseMediaItem(item.id, item))
					.filter(Boolean);
				docs.sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));
				setMediaItems(docs);
			})
			.catch((err) => {
				if (!cancelled) {
					console.error("[Media] fetch failed:", err.message);
					setMediaError(err.message);
				}
			})
			.finally(() => {
				if (!cancelled) setLoadingMedia(false);
			});
		return () => { cancelled = true; };
	}, [retryKey]);

	const filteredMedia = useMemo(() => {
		const query = searchTerm.trim().toLocaleLowerCase();
		return mediaItems.filter((item) => {
			const matchesType = activeType === "all" || item.type === activeType;
			const matchesQuery = !query || [
				item.title,
				item.platform,
				item.description,
			].some((value) => value.toLocaleLowerCase().includes(query));
			return matchesType && matchesQuery;
		});
	}, [activeType, mediaItems, searchTerm]);

	const mediaGroups = useMemo(() => groupMediaByPlatform(filteredMedia), [filteredMedia]);

	const typeCounts = useMemo(() => ({
		all: mediaItems.length,
		video: mediaItems.filter((item) => item.type === "video").length,
		radio: mediaItems.filter((item) => item.type === "radio").length,
		press: mediaItems.filter((item) => item.type === "press").length,
	}), [mediaItems]);

	const currentSEO = SEO.find((item) => item.page === "projects");

	return (
		<React.Fragment>
			<SeoMeta
				title={`Jabu Zwane in the Media | ${INFO.main.title}`}
				description={makeDescription(
					mediaItems[0]?.description,
					currentSEO.description
				)}
				path="/projects"
				image={mediaItems[0]?.type === "video" ? getYouTubeThumbnail(mediaItems[0].url) : undefined}
				keywords={currentSEO.keywords}
				structuredData={{
					"@context": "https://schema.org",
					"@type": "CollectionPage",
					name: "Jabu Zwane in the Media",
					description: makeDescription(
						mediaItems[0]?.description,
						currentSEO.description
					),
					url: `${SITE_URL}/projects`,
					mainEntity: {
						"@type": "ItemList",
						itemListElement: mediaItems.slice(0, 30).map((item, index) => ({
							"@type": "ListItem",
							position: index + 1,
							item: {
								"@type": "CreativeWork",
								name: item.title,
								url: item.url,
								...(item.description ? { description: makeDescription(item.description) } : {}),
								...(item.type === "video" && getYouTubeThumbnail(item.url)
									? { thumbnailUrl: getYouTubeThumbnail(item.url) }
									: {}),
							},
						})),
					},
				}}
			/>
			<div className="page-content">
				<NavBar active="projects" />
				<div className="content-wrapper">
					<div className="projects-logo-container">
						<div className="projects-logo">
							<Logo width={46} />
						</div>
					</div>

					<div className="projects-container">
						<h1 className="title projects-title">
							Jabu Zwane in the media
						</h1>

						<div className="subtitle projects-subtitle">
							Jabu Zwane is regularly invited on various media platforms for his
							expertise as a Mindset Development Specialist and thought leader on
							mindset, leadership, entrepreneurship, and effective communication.
							Below is a selection of platforms where he has been featured or
							interviewed.
						</div>

						{/* ── Legend ──────────────────────────────────────────────────── */}
						{!loadingMedia && !mediaError && mediaItems.length > 0 && (
							<div className="media-controls">
								<label className="media-search">
									<span className="media-search__icon" aria-hidden="true">⌕</span>
									<span className="sr-only">Search media appearances</span>
									<input
										type="search"
										value={searchTerm}
										onChange={(event) => setSearchTerm(event.target.value)}
										placeholder="Search by topic or platform"
									/>
									{searchTerm && (
										<button
											type="button"
											className="media-search__clear"
											onClick={() => setSearchTerm("")}
											aria-label="Clear search"
										>
											×
										</button>
									)}
								</label>
								<div className="media-filters" role="group" aria-label="Filter media appearances">
									<button
										type="button"
										className={`media-filter${activeType === "all" ? " is-active" : ""}`}
										onClick={() => setActiveType("all")}
										aria-pressed={activeType === "all"}
									>
										All <span>{typeCounts.all}</span>
									</button>
									{Object.entries(TYPE_META).map(([type, meta]) => (
										<button
											key={type}
											type="button"
											className={`media-filter${activeType === type ? " is-active" : ""}`}
											onClick={() => setActiveType(type)}
											aria-pressed={activeType === type}
										>
											<span className="media-badge__icon" aria-hidden="true">{meta.icon}</span>
											{meta.label} <span>{typeCounts[type]}</span>
										</button>
									))}
								</div>
							</div>
						)}

						{/* ── Cards ───────────────────────────────────────────────────── */}
						<div className="projects-list media-grid" aria-live="polite" aria-busy={loadingMedia}>
							{loadingMedia ? (
								<div className="media-state" role="status">
									<span className="media-state__spinner" aria-hidden="true" />
									<p>Loading media appearances…</p>
								</div>
							) : mediaError ? (
								<div className="media-state media-state--error" role="alert">
									<p>We couldn’t load media appearances right now.</p>
									<button type="button" className="media-retry" onClick={() => setRetryKey((key) => key + 1)}>
										Try again
									</button>
								</div>
							) : mediaItems.length === 0 ? (
								<div className="media-state">
									<p>No media appearances are available yet. Please check back soon.</p>
								</div>
							) : filteredMedia.length === 0 ? (
								<div className="media-state">
									<p>No appearances match your search. Try another term or filter.</p>
									<button
										type="button"
										className="media-retry"
										onClick={() => { setSearchTerm(""); setActiveType("all"); }}
									>
										Clear filters
									</button>
								</div>
							) : (
								mediaGroups.map((group) => (
									<section className="media-platform-group" key={group.key || "other-platforms"}>
										<div className="media-platform-group__heading">
											<h2>{group.label}</h2>
											<span>{group.items.length} appearance{group.items.length === 1 ? "" : "s"}</span>
										</div>
										<div className="media-platform-group__grid">
											{group.items.map((item) => <MediaCard key={item.id} item={item} />)}
										</div>
									</section>
								))
							)}
						</div>
					</div>

					<div className="page-footer">
						<Footer />
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default Projects;